import { Button, Dropdown, Form, Icon, Row, Text, Input, Select } from "components";
import { get, startCase } from "lodash";
import { getHighest } from "utils/utils";
import colors from "utils/colors";
import styled from "styled-components";
import { useState } from "react";

/**
 * Functional approval rules system that allows creating rules with requirements
 * and evaluates them to determine final approval requirements.
 */
export default function ApprovalRulesSetupDemo() {
  const [rules, setRules] = useState([
    {
      id: 1,
      name: "Finance Review",
      condition: "amount_greater_than",
      conditionValue: "1000",
      hasRequirements: true,
      requirements: [
        { role: "Finance", count: 1, level: "Analyst" }
      ]
    },
    {
      id: 2,
      name: "Marketing Approval",
      condition: "department_equals",
      conditionValue: "marketing",
      hasRequirements: true,
      requirements: [
        { role: "Marketing", count: 1, level: "Director" }
      ]
    }
  ]);

  const [evaluationContext, setEvaluationContext] = useState({
    amount: 1500,
    department: "marketing"
  });

  const conditionOptions = [
    { label: "Amount Greater Than", value: "amount_greater_than" },
    { label: "Department Equals", value: "department_equals" },
    { label: "Always True", value: "always_true" },
    { label: "Priority High", value: "priority_high" }
  ];

  const roleOptions = [
    { label: "Finance", value: "Finance" },
    { label: "Marketing", value: "Marketing" },
    { label: "Legal", value: "Legal" },
    { label: "Management", value: "Management" }
  ];

  const levelOptions = [
    { label: "Analyst", value: "Analyst" },
    { label: "Manager", value: "Manager" },
    { label: "Director", value: "Director" },
    { label: "VP", value: "VP" }
  ];

  const addRule = () => {
    const newRule = {
      id: getHighest(rules, "id") + 1,
      name: "New Rule",
      condition: "always_true",
      conditionValue: "",
      hasRequirements: false,
      requirements: []
    };
    setRules([...rules, newRule]);
  };

  const updateRule = (ruleId, updates) => {
    setRules(rules.map(rule => 
      rule.id === ruleId ? { ...rule, ...updates } : rule
    ));
  };

  const removeRule = (ruleId) => {
    setRules(rules.filter(rule => rule.id !== ruleId));
  };

  const addRequirement = (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    const newRequirement = { role: "Finance", count: 1, level: "Analyst" };
    updateRule(ruleId, {
      requirements: [...rule.requirements, newRequirement]
    });
  };

  const updateRequirement = (ruleId, reqIndex, updates) => {
    const rule = rules.find(r => r.id === ruleId);
    const newRequirements = rule.requirements.map((req, index) =>
      index === reqIndex ? { ...req, ...updates } : req
    );
    updateRule(ruleId, { requirements: newRequirements });
  };

  const removeRequirement = (ruleId, reqIndex) => {
    const rule = rules.find(r => r.id === ruleId);
    const newRequirements = rule.requirements.filter((_, index) => index !== reqIndex);
    updateRule(ruleId, { requirements: newRequirements });
  };

  // Rule evaluation logic
  const evaluateRule = (rule, context) => {
    let result = false;
    
    switch (rule.condition) {
      case "amount_greater_than":
        result = context.amount > parseFloat(rule.conditionValue || 0);
        break;
      case "department_equals":
        result = context.department === rule.conditionValue;
        break;
      case "always_true":
        result = true;
        break;
      case "priority_high":
        result = context.priority === "high";
        break;
      default:
        result = false;
    }

    return {
      result,
      requirements: rule.hasRequirements && result ? rule.requirements : null
    };
  };

  // Evaluate all rules and combine requirements
  const evaluateAllRules = () => {
    const evaluatedRules = rules.map(rule => ({
      ...rule,
      evaluation: evaluateRule(rule, evaluationContext)
    }));

    const approvalRequired = evaluatedRules.some(rule => rule.evaluation.result);
    
    const finalRequirements = evaluatedRules
      .filter(rule => rule.evaluation.result && rule.evaluation.requirements)
      .flatMap(rule => rule.evaluation.requirements)
      .reduce((acc, req) => {
        const existing = acc.find(r => r.role === req.role && r.level === req.level);
        if (existing) {
          existing.count = Math.max(existing.count, req.count);
        } else {
          acc.push({ ...req });
        }
        return acc;
      }, []);

    return {
      approvalRequired,
      requirements: finalRequirements,
      evaluatedRules
    };
  };

  const evaluation = evaluateAllRules();

  return (
    <Container>
      <Card>
        <Header>
          <Text
            data={{
              text: "Configure Approval Rules",
              fontSize: 20,
              fontWeight: 700,
            }}
          />
          <Button
            data={{
              text: "Add Rule",
              icon: "FiPlus",
              size: "small",
              onClick: addRule
            }}
          />
        </Header>

        <RulesContainer>
          {rules.map((rule) => (
            <RuleCard key={rule.id}>
              <RuleHeader>
                <Input
                  data={{
                    value: rule.name,
                    placeholder: "Rule Name",
                    onChange: (v) => updateRule(rule.id, { name: v }),
                    inputHeight: "32px"
                  }}
                />
                <Dropdown
                  data={{
                    icon: {
                      icon: "FiMoreHorizontal",
                      hover: true,
                      size: 20,
                      color: colors.grey,
                    },
                    options: [
                      { 
                        label: "Delete", 
                        icon: "FiTrash",
                        onClick: () => removeRule(rule.id)
                      },
                    ],
                  }}
                />
              </RuleHeader>

              <ConditionRow>
                <Select
                  data={{
                    value: rule.condition,
                    options: conditionOptions,
                    onChange: (v) => updateRule(rule.id, { condition: v }),
                    inputHeight: "36px"
                  }}
                />
                {rule.condition !== "always_true" && rule.condition !== "priority_high" && (
                  <Input
                    data={{
                      value: rule.conditionValue,
                      placeholder: "Value",
                      onChange: (v) => updateRule(rule.id, { conditionValue: v }),
                      inputHeight: "36px"
                    }}
                  />
                )}
                <EvaluationBadge isTrue={evaluateRule(rule, evaluationContext).result}>
                  {evaluateRule(rule, evaluationContext).result ? "TRUE" : "FALSE"}
                </EvaluationBadge>
              </ConditionRow>

              <RequirementsSection>
                <RequirementsHeader>
                  <Text data={{ text: "Requirements", fontSize: 14, fontWeight: 600 }} />
                  <ToggleSwitch
                    active={rule.hasRequirements}
                    onClick={() => updateRule(rule.id, { hasRequirements: !rule.hasRequirements })}
                  >
                    {rule.hasRequirements ? "ON" : "OFF"}
                  </ToggleSwitch>
                </RequirementsHeader>

                {rule.hasRequirements && (
                  <>
                    {rule.requirements.map((req, index) => (
                      <RequirementRow key={index}>
                        <Input
                          data={{
                            value: req.count.toString(),
                            type: "number",
                            onChange: (v) => updateRequirement(rule.id, index, { count: parseInt(v) || 1 }),
                            inputHeight: "32px",
                            width: "60px"
                          }}
                        />
                        <Select
                          data={{
                            value: req.role,
                            options: roleOptions,
                            onChange: (v) => updateRequirement(rule.id, index, { role: v }),
                            inputHeight: "32px"
                          }}
                        />
                        <Select
                          data={{
                            value: req.level,
                            options: levelOptions,
                            onChange: (v) => updateRequirement(rule.id, index, { level: v }),
                            inputHeight: "32px"
                          }}
                        />
                        <Button
                          data={{
                            icon: "FiX",
                            type: "basic",
                            size: "small",
                            onClick: () => removeRequirement(rule.id, index)
                          }}
                        />
                      </RequirementRow>
                    ))}
                    <AddRequirementButton onClick={() => addRequirement(rule.id)}>
                      <Icon data={{ icon: "FiPlus", size: 14 }} />
                      Add Requirement
                    </AddRequirementButton>
                  </>
                )}
              </RequirementsSection>
            </RuleCard>
          ))}
        </RulesContainer>
      </Card>

      <EvaluationCard>
        <Text
          data={{
            text: "Test Context",
            fontSize: 18,
            fontWeight: 600,
            margin: "0 0 15px 0"
          }}
        />
        <TestContext>
          <Input
            data={{
              label: "Amount",
              value: evaluationContext.amount.toString(),
              type: "number",
              onChange: (v) => setEvaluationContext({...evaluationContext, amount: parseFloat(v) || 0}),
              inputHeight: "36px"
            }}
          />
          <Select
            data={{
              label: "Department",
              value: evaluationContext.department,
              options: [
                { label: "Marketing", value: "marketing" },
                { label: "Finance", value: "finance" },
                { label: "Legal", value: "legal" }
              ],
              onChange: (v) => setEvaluationContext({...evaluationContext, department: v}),
              inputHeight: "36px"
            }}
          />
        </TestContext>

        <EvaluationResult>
          <Text
            data={{
              text: "Final Evaluation",
              fontSize: 16,
              fontWeight: 600,
            }}
          />
          <ResultBadge isApprovalRequired={evaluation.approvalRequired}>
            {evaluation.approvalRequired ? "APPROVAL REQUIRED" : "NO APPROVAL NEEDED"}
          </ResultBadge>
          
          {evaluation.requirements.length > 0 && (
            <RequirementsList>
              <Text data={{ text: "Required Approvals:", fontSize: 14, fontWeight: 500 }} />
              {evaluation.requirements.map((req, index) => (
                <RequirementItem key={index}>
                  {req.count} {req.role} {req.level}{req.count > 1 ? 's' : ''}
                </RequirementItem>
              ))}
            </RequirementsList>
          )}
        </EvaluationResult>
      </EvaluationCard>
    </Container>
  );
}

/* --------------------------------- Styles -------------------------------- */
const Container = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const Card = styled.div`
  border: 1px solid ${colors.inputBorder};
  border-radius: 12px;
  background: #fff;
  padding: 24px;
  flex: 1;
  width: 100%;
`;

const EvaluationCard = styled(Card)`
  // max-width: 400px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const RulesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const RuleCard = styled.div`
  border: 1px solid ${colors.inputBorder};
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
`;

const RuleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
`;

const ConditionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const EvaluationBadge = styled.span`
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  background: ${props => props.isTrue ? '#22c55e' : '#ef4444'};
  color: white;
`;

const RequirementsSection = styled.div`
  border-top: 1px solid ${colors.inputBorder};
  padding-top: 12px;
`;

const RequirementsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ToggleSwitch = styled.button`
  padding: 4px 8px;
  border-radius: 4px;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${props => props.active ? '#3b82f6' : '#6b7280'};
  color: white;
`;

const RequirementRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const AddRequirementButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px dashed ${colors.inputBorder};
  border-radius: 4px;
  padding: 8px 12px;
  color: ${colors.grey};
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const TestContext = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const EvaluationResult = styled.div`
  border-top: 1px solid ${colors.inputBorder};
  padding-top: 16px;
`;

const ResultBadge = styled.span`
  display: inline-block;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  margin: 8px 0;
  background: ${props => props.isApprovalRequired ? '#fbbf24' : '#22c55e'};
  color: white;
`;

const RequirementsList = styled.div`
  margin-top: 12px;
`;

const RequirementItem = styled.div`
  background: #e5e7eb;
  padding: 4px 8px;
  border-radius: 4px;
  margin: 4px 0;
  font-size: 13px;
`;
